import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crown, CheckCircle, Loader2, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n";

export const SubscriptionModal = ({
  open,
  onOpenChange,
  subscriptionPlans,
  userSubscription,
  onSubscribe,
  onCancel,
  isProcessingPayment
}) => {
  const { t } = useLanguage();
  const [interval, setInterval] = useState("month");

  const { visiblePlans, hasYearly } = useMemo(() => {
    const all = subscriptionPlans || [];
    const yearly = all.some((p) => p.interval === "year");
    const visible = all.filter((p) => (p.interval || "month") === interval);
    return { visiblePlans: visible.length ? visible : all, hasYearly: yearly };
  }, [subscriptionPlans, interval]);

  const priceLabel = (plan) => (plan.interval === "year"
    ? t("per_year_short")
    : t("per_month_short"));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Crown size={20} className="text-yellow-400" /> {t('subscription')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {userSubscription?.status === "active" ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-400 font-semibold">{userSubscription.plan_name}</p>
                  <p className="text-sm text-zinc-400">{userSubscription.credits_per_month} {t('credits_per_month')}</p>
                </div>
                <Button
                  data-testid="subscription-cancel-btn"
                  onClick={onCancel}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  {t('cancel_subscription')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <p className="text-zinc-400 text-sm">{t('choose_plan')}</p>
                {hasYearly && (
                  <div
                    data-testid="billing-interval-toggle"
                    className="inline-flex bg-zinc-800 rounded-full p-1 border border-zinc-700"
                  >
                    <button
                      type="button"
                      data-testid="billing-monthly-btn"
                      onClick={() => setInterval("month")}
                      className={`px-4 py-1.5 text-sm rounded-full transition-all ${
                        interval === "month"
                          ? "bg-purple-600 text-white shadow"
                          : "text-zinc-400 hover:text-white"
                      }`}
                    >
                      {t('billing_monthly')}
                    </button>
                    <button
                      type="button"
                      data-testid="billing-yearly-btn"
                      onClick={() => setInterval("year")}
                      className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center gap-1.5 ${
                        interval === "year"
                          ? "bg-purple-600 text-white shadow"
                          : "text-zinc-400 hover:text-white"
                      }`}
                    >
                      {t('billing_yearly')}
                      <span
                        data-testid="billing-yearly-badge"
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                          interval === "year"
                            ? "bg-yellow-400 text-zinc-900"
                            : "bg-emerald-500/20 text-emerald-400"
                        }`}
                      >
                        {t('yearly_save_badge')}
                      </span>
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {visiblePlans.map((plan) => {
                  const isYear = plan.interval === "year";
                  const monthlyEquiv = plan.monthly_equivalent
                    ?? (isYear ? +(plan.price / 12).toFixed(2) : null);
                  const nameLabel = plan.name_key
                    ? (t(plan.name_key) === plan.name_key ? plan.name : t(plan.name_key))
                    : plan.name;
                  return (
                    <div
                      key={plan.id}
                      data-testid={`plan-card-${plan.id}`}
                      className={`p-4 rounded-lg border transition-all relative ${
                        isYear
                          ? "border-yellow-500/40 bg-gradient-to-br from-purple-500/5 to-yellow-500/5 hover:border-yellow-500/60"
                          : "border-zinc-700 hover:border-zinc-600"
                      }`}
                    >
                      {isYear && plan.discount_percent && (
                        <div className="absolute -top-2 -right-2 bg-yellow-400 text-zinc-900 text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-lg">
                          <Sparkles size={10} /> {t('save_percent', { percent: plan.discount_percent })}
                        </div>
                      )}
                      <div className="text-center mb-3">
                        <div className="text-lg font-bold text-white">{nameLabel}</div>
                        <div className="text-3xl font-bold text-purple-400 mt-1">
                          ₺{plan.price}
                          <span className="text-sm text-zinc-500">/{priceLabel(plan)}</span>
                        </div>
                        {isYear && monthlyEquiv && (
                          <div className="text-xs text-emerald-400 mt-1">
                            ≈ ₺{monthlyEquiv} {t('per_month_avg_short')}
                          </div>
                        )}
                      </div>
                      <div className="space-y-2 mb-4">
                        {(plan.feature_keys || []).map((fk, i) => {
                          const translated = t(fk);
                          const display = translated === fk ? (plan.features?.[i] || fk) : translated;
                          return (
                            <div key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                              <CheckCircle size={14} className="text-green-400" />
                              {display}
                            </div>
                          );
                        })}
                        {(!plan.feature_keys || plan.feature_keys.length === 0) && plan.features?.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                            <CheckCircle size={14} className="text-green-400" />
                            {f}
                          </div>
                        ))}
                      </div>
                      <Button
                        data-testid={`plan-subscribe-btn-${plan.id}`}
                        onClick={() => onSubscribe(plan.id)}
                        disabled={isProcessingPayment}
                        className={`w-full ${
                          isYear
                            ? "bg-gradient-to-r from-purple-600 to-yellow-500 hover:from-purple-700 hover:to-yellow-600"
                            : "bg-purple-600 hover:bg-purple-700"
                        }`}
                      >
                        {isProcessingPayment ? <Loader2 size={16} className="animate-spin" /> : t('subscribe')}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
